import { useEffect, useState } from "react";

import StepOne from "../components/StepOne";
import StepTwo from "../components/StepTwo";
import { Category } from "../types/Category";
import { Report } from "../types/Report";
import Sidebar from "../components/HomePage/Sidebar";
import CreateCategory from "../components/HomePage/CreateCategory";
import { useForm } from "react-hook-form";
import api from "../axios";

const HomePage = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState("categories"); // To track the current tab
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Report>>();

  const {
    watch,
    control,
    handleSubmit: handleReportSubmit,
    setValue,
    reset,
  } = useForm<Partial<Report>>({
    defaultValues: {
      report_url: "",
      report_name: "",
      report_type: "",
      is_competition: false,
      image_url: "",
      description: "",
      category_id: undefined,
      alias: "",
      tutorial_video_link: "",
      is_featured_competition: false,
    },
  });

  // Fetch categories from the backend
  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories/");
      setCategories(response.data);
    } catch (err: any) {
      console.error("Error fetching categories:", err);
      const message = err.response?.data?.detail || err.message || "An error occurred while fetching categories";
      setError(message);
    }
  };

  // Fetch reports from the backend
  const fetchReports = async () => {
    try {
      const response = await api.get("/reports/");
      setReports(response.data);
    } catch (err) {
      console.error("Error fetching reports:", err);
    }
  };

  // Handle form submission for adding new categories
  const handleSubmit = async (data: Partial<Category>) => {
    setError(null);
    setSuccess(null);

    try {
      await api.post("/categories/", data);
      setSuccess("Category added successfully!");
      fetchCategories();
    } catch (err: any) {
      const message = err.response?.data?.detail?.[0]?.msg ?? err.response?.data?.detail ?? (err.message || "An error occurred");
      setError(message);
      throw new Error(message);
    }
  };

  const handleUpdateCategories = async (reorderedCategories: Category[]) => {
    const updateCategories = reorderedCategories.map((c) => ({
      id: c.id,
      position: c.position,
    }));

    setCategories([
      ...categories.filter(
        (c) => reorderedCategories.findIndex((cc) => cc.id === c.id) === -1
      ),
      ...reorderedCategories,
    ]);

    try {
      await api.patch("/categories/", updateCategories);
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || "An error occurred";
      setError(message);
    }
  };

  const handleUpdateReports = async (reorderedReports: Report[]) => {
    const updateReports = reorderedReports.map((c) => ({
      id: c.id,
      position: c.position,
      category_id: c.category.id,
    }));

    setReports([
      ...reports.filter(
        (r) => reorderedReports.findIndex((rr) => rr.id === r.id) === -1
      ),
      ...reorderedReports,
    ]);

    try {
      await api.patch("/reports/", updateReports);
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || "An error occurred";
      setError(message);
    }
  };

  const handleUpdateCategory = async (
    id: number,
    category: Partial<Category>
  ) => {
    try {
      await api.patch(`/categories/${id}/`, category);
      setCategories((categories) =>
        categories.map((c) => ({
          ...c,
          ...(c.id === id ? category : {}),
        }))
      );
      setReports((reports) =>
        reports.map((r) => ({
          ...r,
          category:
            r.category.id === id
              ? {
                ...r.category,
                ...category,
              }
              : r.category,
        }))
      );
      setError("");
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || "An error occurred";
      setError(message);
    }
  };

  const handleUpdateReport = async (id: number, report: Partial<Report>) => {
    const category = categories.find(
      (c) => c.id === +(report.category_id ?? 0)
    );

    if (!category) {
      return;
    }
    const currentReport = reports.find((r) => r.id === id);

    if (!currentReport) {
      return;
    }
    report.position =
      currentReport.category.id === category.id
        ? currentReport.position
        : ([...reports]
          .filter((r) => r.category.id === category.id)
          .sort((r1, r2) => r1.position - r2.position)
          .pop()?.position ?? 0) + 1;

    try {
      await api.patch(`/reports/${id}/`, report);
      setReports((reports) =>
        reports.map((r) => ({
          ...r,
          ...(r.id === id
            ? {
              ...report,
              category,
            }
            : {}),
        }))
      );
      setError("");
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || "An error occurred";
      setError(message);
    }
  };

  const handleDeleteCategory = async (category: Category) => {
    try {
      await api.delete(`/categories/${category.id}/`);
      fetchCategories();
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || "An error occurred";
      setError(message);
    }
  };

  const handleDeleteReport = async (report: Report) => {
    try {
      await api.delete(`/reports/${report.id}/`);
      fetchReports();
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || "An error occurred";
      setError(message);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue("category_id", "" as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("is_competition")]);

  // Step navigation logic for Report Wizard
  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const handleNextStep = (data: Partial<Report>) => {
    setFormData(data);
    nextStep();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 2:
        return (
          formData && (
            <StepTwo
              prevStep={prevStep}
              categories={categories}
              formData={formData}
              reset={reset}
            />
          )
        );
      default:
        return (
          <StepOne
            control={control}
            report_type={watch("report_type") ?? ""}
            is_competition={watch("is_competition") ?? false}
            nextStep={handleReportSubmit(handleNextStep)}
            categories={categories}
          />
        );
    }
  };

  return (
    <div className="flex lg:flex-row flex-col gap-2 px-4">
      <div className="lg:h-screen">
        <Sidebar
          categories={categories}
          reports={reports}
          onCategoryUpdate={handleUpdateCategory}
          onCategoriesUpdate={handleUpdateCategories}
          onReportsUpdate={handleUpdateReports}
          onDeleteCategory={handleDeleteCategory}
          onDeleteReport={handleDeleteReport}
          onReportUpdate={handleUpdateReport}
          onReportQueryUpdate={fetchReports}
        />
      </div>

      <div className="bg-white shadow mx-auto my-4 p-3 md:p-6 border border-slate-100 rounded container">
        <h1 className="mb-4 font-bold text-xl md:text-2xl">
          Report & Category Management
        </h1>

        {/* Tab Navigation */}
        <div className="flex mb-4 border-b">
          <button
            className={`px-4 py-2 ${currentTab === "categories" ? "border-b-2 border-blue-500" : ""
              }`}
            onClick={() => setCurrentTab("categories")}
          >
            Manage Categories
          </button>
          <button
            className={`px-4 py-2 ${currentTab === "report" ? "border-b-2 border-blue-500" : ""
              }`}
            onClick={() => setCurrentTab("report")}
          >
            Report Wizard
          </button>
        </div>

        {/* Tab Panels */}
        {currentTab === "categories" && (
          <div>
            {/* Display success or error messages */}
            {success && (
              <p className="bg-green-50 mb-4 p-3 border-green-100 rounded-md text-green-500">
                {success}
              </p>
            )}
            {error && (
              <p className="bg-red-50 mb-4 p-3 border border-red-100 rounded-md text-red-500">
                {error}
              </p>
            )}

            {/* Form to add a new category */}
            <CreateCategory onSubmit={handleSubmit} />
          </div>
        )}

        {currentTab === "report" && (
          <div>
            {/* Report Wizard Section */}
            <h2 className="mb-4 font-semibold text-xl">
              Embed Tableau Report Wizard
            </h2>
            {renderStep()}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
